import { useState } from "react";
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@shared-components/shadcn/chart";
import { api } from "@shared-server/api";
import {
  CartesianGrid,
  XAxis,
  YAxis,
  Legend,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Tooltip as RechartsTooltip,
} from "recharts";
import { Checkbox } from "@shared-components/shadcn/checkbox";
import { Button } from "@shared-components/shadcn/button";
import { formatGranularDateTick } from "@shared-components/core-metrics/metric-utils";
import type { GranularityType } from "@shared-server/api/routers/queries/metrics/utils";
import {
  type PluginTypeEnumTypes,
  PluginTypeEnumValues,
} from "@chat-ai/_database/enums";
import { pluginsMetadata } from "@chat-ai/_database/models/plugins-metadata";

const chartConfig: ChartConfig = {
  count: {
    label: "Plugins count in chats",
  },
};

interface PluginUsageChartProps {
  startDate: Date;
  endDate: Date;
  granularity: GranularityType;
}

export const PluginUsageChart = ({
  startDate,
  endDate,
  granularity,
}: PluginUsageChartProps) => {
  const pluginsQuery = api.coreMetrics.pluginsEnabledPerDay.useQuery({
    startDate,
    endDate,
    granularity,
  });
  type PluginDataItem = { date: string } & Record<string, number>;
  const chartData = (pluginsQuery.data as PluginDataItem[]) ?? [];

  const allPlugins = PluginTypeEnumValues;

  const pluginTotals = {} as Record<PluginTypeEnumTypes, number>;
  allPlugins.forEach((plugin) => {
    pluginTotals[plugin] = chartData.reduce(
      (sum, item) => sum + Number(item[plugin] ?? 0),
      0,
    );
  });

  const pieChartData = allPlugins
    .filter((plugin) => pluginTotals[plugin] > 0)
    .map((plugin) => ({
      plugin: plugin,
      name: pluginsMetadata(plugin).name,
      value: pluginTotals[plugin],
      color: pluginsMetadata(plugin).metricColor,
    }));

  const [visiblePlugins, setVisiblePlugins] = useState<PluginTypeEnumTypes[]>(
    PluginTypeEnumValues.map((p) => p),
  );

  const handleToggle = (plugin: PluginTypeEnumTypes) => {
    setVisiblePlugins((prev) =>
      prev.includes(plugin)
        ? prev.filter((p) => p !== plugin)
        : [...prev, plugin],
    );
  };

  const handleEnablePiePlugins = () => {
    setVisiblePlugins(
      pieChartData.map((entry) => {
        return entry.plugin;
      }),
    );
  };

  const normalizedChartData = chartData.map((item) => {
    const filled: Record<string, number | string> = { ...item };
    allPlugins.forEach((plugin) => {
      if (typeof filled[plugin] !== "number") {
        filled[plugin] = 0;
      }
    });
    return filled;
  });

  return (
    <div>
      <h2 className="mb-4">Plugin usage per day</h2>
      {chartData.length !== 0 && (
        <>
          <div className="mb-4 flex flex-wrap gap-3">
            {allPlugins.map((plugin) => (
              <label
                key={plugin}
                className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 hover:bg-gray-50"
              >
                <Checkbox
                  checked={visiblePlugins.includes(plugin)}
                  onCheckedChange={() => handleToggle(plugin)}
                  className="accent-current"
                />
                <span>{pluginsMetadata(plugin).name}</span>
              </label>
            ))}
            <Button onClick={handleEnablePiePlugins}>
              {"Enable plugins for selected time"}
            </Button>
          </div>
          <div className="mt-8 flex h-[300px] w-full items-start">
            <div className="flex h-full flex-1 flex-col">
              <ChartContainer
                config={chartConfig}
                className="aspect-auto h-full flex-1"
              >
                <LineChart
                  data={normalizedChartData}
                  margin={{ top: 10, right: 30, left: 20, bottom: 10 }}
                  height={260}
                >
                  <CartesianGrid vertical={false} />
                  <XAxis
                    dataKey="date"
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    fill="#006e96"
                    minTickGap={32}
                    tickFormatter={(value: string) =>
                      formatGranularDateTick(value, granularity)
                    }
                  />
                  <YAxis yAxisId="count" interval={1} orientation="left" />
                  <ChartTooltip
                    content={
                      <ChartTooltipContent
                        className="w-[150px]"
                        nameKey="date"
                        labelFormatter={(value: string) => value}
                      />
                    }
                  />
                  {visiblePlugins.map((plugin) => (
                    <Line
                      key={plugin}
                      yAxisId="count"
                      type="monotone"
                      dataKey={plugin}
                      name={pluginsMetadata(plugin).name}
                      stroke={pluginsMetadata(plugin).metricColor}
                      strokeWidth={2}
                      dot={false}
                    />
                  ))}
                </LineChart>
              </ChartContainer>
              <div className="mt-2 flex h-[32px] flex-wrap items-center justify-center">
                {visiblePlugins.map((plugin) => (
                  <span key={plugin} className="mr-4 flex items-center text-sm">
                    <span
                      className="mr-1 inline-block h-3 w-3 rounded-full"
                      style={{
                        background: pluginsMetadata(plugin).metricColor,
                      }}
                    />
                    {pluginsMetadata(plugin).name}
                  </span>
                ))}
              </div>
            </div>
          </div>
          <div className="flex items-center justify-center">
            <PieChart width={600} height={400}>
              <Pie
                data={pieChartData.filter((entry) =>
                  visiblePlugins.includes(entry.plugin),
                )}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={60}
                label={({ name, value }) => `${name}: (${value})`}
              >
                {pieChartData.map((entry, idx) => (
                  <Cell key={`cell-${idx}`} fill={entry.color} />
                ))}
              </Pie>
              <RechartsTooltip />
              <Legend />
            </PieChart>
          </div>
        </>
      )}
    </div>
  );
};
